import type { Nullable } from '@iheartjane/types';
import type { QueryClient } from '@tanstack/react-query';
import compact from 'lodash-es/compact';
import type { LoaderFunctionArgs } from 'react-router';
import { redirect } from 'react-router';

import { getJwtQueryOptions } from '@jane/kiosk/data-access/lib/queries/jwt';
import { getKioskQueryOptions } from '@jane/kiosk/data-access/lib/queries/kiosk';
import { getWhoamiQueryOptions } from '@jane/kiosk/data-access/lib/queries/whoami';
import { ensureNumericId } from '@jane/kiosk/util';
import { getStoreQueryOptions } from '@jane/shared/data-access';
import type { Kiosk, Store } from '@jane/shared/types';

export type RootLoaderType = {
  kiosk: Nullable<Kiosk>;
  kioskId: number;
  store: Store;
  storeId: number;
  /** If the browser is authenticated, this is identical to the 'kiosk' response. */
  whoami: Nullable<Kiosk>;
};

export const rootLoader =
  (queryClient: QueryClient) =>
  async ({
    params,
  }: LoaderFunctionArgs): Promise<Response | RootLoaderType> => {
    const { storeId, cartUuid } = params;
    const numericStoreId = ensureNumericId(storeId);

    const store = await queryClient.query({
      staleTime: 'static',
      ...getStoreQueryOptions<Store>(numericStoreId),
    });
    const kioskId = ensureNumericId(store.kiosk_id);
    const kiosk = await queryClient.query({
      staleTime: 'static',
      ...getKioskQueryOptions(kioskId),
    });

    const jwt = await queryClient.query({
      staleTime: 'static',
      ...getJwtQueryOptions(),
    });
    const whoami = await queryClient.query({
      staleTime: 'static',
      ...getWhoamiQueryOptions(jwt, cartUuid),
    });

    const kioskStoreIds = compact(whoami?.stores).map(({ id }) => Number(id));
    if (whoami?.enabled && !kioskStoreIds.includes(numericStoreId)) {
      return redirect(`/stores/${kioskStoreIds[0]}`);
    }

    return {
      kiosk,
      kioskId,
      store,
      storeId: numericStoreId,
      whoami,
    };
  };
